import { type FilterState, state } from "../state";
import { dom } from "../dom";
import { getActiveFragmentsModel } from "../fragments/engine";
import { refreshHiddenGhost } from "../features/selection";
import { ifcTypeLabel } from "../i18n";

async function rebuildFragmentsVisibility(): Promise<void> {
  const modelId = state.fragmentsModelId;
  if (!modelId) {
    return;
  }
  const model = getActiveFragmentsModel(modelId);
  if (!model) {
    return;
  }
  await model.resetVisible();
  if (state.fragmentsModelId !== modelId) {
    return;
  }
  const allIds = state.geometryExpressIds || [];
  const toHide = new Set<number>();
  for (const id of allIds) {
    if (
      state.hiddenExpressIds.has(id) ||
      state.transparentSpaceIds.has(id) ||
      !isPassingFilters(id)
    ) {
      toHide.add(id);
    }
  }
  if (state.planView.active && state.planView.storeyName) {
    const selected = state.planView.storeyName;
    for (const id of allIds) {
      const name = state.elementMeta.get(id)?.storeyName || "";
      if (name && name !== selected) {
        toHide.add(id);
      }
    }
  }
  if (toHide.size > 0) {
    await model.setVisible([...toHide], false);
  }
  if (state.fragmentsModelId !== modelId) {
    return;
  }
  refreshHiddenGhost();
}

export function setFilterControlsEnabled(enabled: boolean): void {
  dom.filterStoreySelect.disabled = !enabled;
  dom.filterTypeSelect.disabled = !enabled;
  dom.filterMarkInput.disabled = !enabled;
  dom.applyFiltersButton.disabled = !enabled;
  dom.clearFiltersButton.disabled = !enabled;
}

export function updateFilterSelectOptions(
  selectElement: HTMLSelectElement,
  values: string[],
  firstLabel: string,
  labelFn?: (value: string) => string,
): void {
  const currentValues = selectElement.multiple
    ? [...selectElement.selectedOptions].map((opt) => opt.value)
    : [selectElement.value];
  selectElement.innerHTML = selectElement.multiple ? "" : `<option value="">${firstLabel}</option>`;
  values.forEach((value) => {
    const option = document.createElement("option");
    option.value = value;
    // value хранит исходный код (IFCDOOR), текст — локализованную подпись.
    option.textContent = labelFn ? labelFn(value) : value;
    if (currentValues.includes(value)) {
      option.selected = true;
    }
    selectElement.appendChild(option);
  });
  if (!selectElement.multiple && [...selectElement.options].some((opt) => opt.value === currentValues[0])) {
    selectElement.value = currentValues[0];
  }
}

/**
 * Переразмечает подписи опций в селекте типов под текущий язык, не трогая
 * их value (исходные IFC-коды) и сохранённый выбор. Вызывается при смене языка.
 */
export function relabelFilterTypeOptions(): void {
  for (const option of [...dom.filterTypeSelect.options]) {
    if (option.value) {
      option.textContent = ifcTypeLabel(option.value);
    }
  }
}

export function resetFilterUiAndState(): void {
  state.activeFilters.storeyId = "";
  state.activeFilters.types = [];
  state.activeFilters.mark = "";
  dom.filterStoreySelect.innerHTML = '<option value="">Все этажи</option>';
  dom.filterTypeSelect.innerHTML = "";
  dom.filterMarkInput.value = "";
  setFilterControlsEnabled(false);
}

export function isPassingFilters(expressId: number): boolean {
  if (!state.ifcModel) {
    return false;
  }
  const f = state.activeFilters;
  if (!f.storeyId && f.types.length === 0 && !f.mark) {
    return true;
  }
  const meta = state.elementMeta.get(expressId) || { type: "", mark: "", storeyName: "" };
  if (f.storeyId && meta.storeyName !== f.storeyId) {
    return false;
  }
  if (f.types.length > 0 && !f.types.includes(meta.type)) {
    return false;
  }
  if (f.mark) {
    const haystack = `${meta.mark}`.toLowerCase();
    if (!haystack.includes(f.mark.toLowerCase())) {
      return false;
    }
  }
  return true;
}

export function updateTransparentSpaceIdsFromMeta(): void {
  state.transparentSpaceIds.clear();
  for (const [id, meta] of state.elementMeta) {
    if ((meta?.type || "").toUpperCase() === "IFCSPACE") {
      state.transparentSpaceIds.add(id);
    }
  }
}

export function removeTransparentSpaceMesh(): void {
  if (!state.transparentSpaceMesh) {
    return;
  }
  state.transparentSpaceMesh.parent?.remove(state.transparentSpaceMesh);
  state.transparentSpaceMesh.geometry?.dispose?.();
  state.transparentSpaceMesh = null;
}

export function refreshTransparentSpaceMesh(): void {
  removeTransparentSpaceMesh();
}

export function applyFilterStateToUi(filterState: FilterState | undefined): void {
  const st = filterState || { storeyId: "", types: [], mark: "" };
  dom.filterStoreySelect.value = st.storeyId || "";
  const typesSet = new Set((st.types || []).filter(Boolean));
  [...dom.filterTypeSelect.options].forEach((opt) => {
    opt.selected = typesSet.has(opt.value);
  });
  dom.filterMarkInput.value = st.mark || "";
  state.activeFilters.storeyId = dom.filterStoreySelect.value || "";
  state.activeFilters.types = [...typesSet];
  state.activeFilters.mark = dom.filterMarkInput.value.trim();
}

let visibilityQueue: Promise<void> = Promise.resolve();

export function rebuildMeshVisibility(): void {
  if (!state.ifcModel || !state.fragmentsModelId) {
    return;
  }
  visibilityQueue = visibilityQueue.catch(() => undefined).then(() => rebuildFragmentsVisibility());
}

export function captureGeometrySnapshot(): void {
  /* Fragments-only runtime: no geometry snapshot. */
}
